import React, { useState } from "react";
import {
  ChangeShippingOptions,
  ShippingRegionOption,
  Warehouse,
  formControlRegionName,
  formControlShippingOptionName
} from "../../shared/models/customerShippingOptions";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  ThemeProvider,
  createTheme
} from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import moment from "moment";
import { ShippingOptionCode, ShippingRegionCode, compressWeekDays } from "../../../shared/models/models";
import { TabContext, TabList, useTabContext } from "@mui/lab";
import { changeShippingOptions, getShippingOptions } from "../customerShippingOptionsSlice";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import colors from "../../../shared/themes/colors";
import { useNavigate } from "react-router-dom";
import ShippingOptionStandartOrExpress from "./ShippingOptionStandartOrExpress";
import { CurrencyCode } from "../../../common/models/CommonModels";
import useTranslation from "../../../shared/hooks/useTranslation";

export interface ShippingDialogProps {
  open: boolean;
  onClose: () => void;
  shippingOptions?: ShippingRegionOption[];
  countryId: number;
  warehouse?: Warehouse;
}

function TabPanelCustom(props: any) {
  const { children, value } = props;
  const context = useTabContext();
  const tabId = context?.value;
  return (
    <Box
      className="flex flex-col gap-2 mt-6"
      component="div"
      role="tabpanel"
      sx={{
        display: value === tabId ? "flex" : "none"
      }}
    >
      {children}
    </Box>
  );
}

const ShippingDialog = ({ open, onClose, shippingOptions, countryId, warehouse }: ShippingDialogProps) => {
  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors, isValid }
  } = useForm<ChangeShippingOptions>({
    mode: "onChange",
    defaultValues: {
      standartShippingOption: {
        country: shippingOptions
          ?.filter((x) => x.typeCode === ShippingOptionCode.standart)
          .find((x) => x.regionCode === ShippingRegionCode.country),
        europeanUnion: shippingOptions
          ?.filter((x) => x.typeCode === ShippingOptionCode.standart)
          .find((x) => x.regionCode === ShippingRegionCode.EU),
        worldwide: shippingOptions
          ?.filter((x) => x.typeCode === ShippingOptionCode.standart)
          .find((x) => x.regionCode === ShippingRegionCode.worldwide)
      },
      expressShippingOption: {
        country: shippingOptions
          ?.filter((x) => x.typeCode === ShippingOptionCode.express)
          .find((x) => x.regionCode === ShippingRegionCode.country),
        europeanUnion: shippingOptions
          ?.filter((x) => x.typeCode === ShippingOptionCode.express)
          .find((x) => x.regionCode === ShippingRegionCode.EU),
        worldwide: shippingOptions
          ?.filter((x) => x.typeCode === ShippingOptionCode.express)
          .find((x) => x.regionCode === ShippingRegionCode.worldwide)
      },
      isSelfPickUpActive: shippingOptions
        ?.filter((x) => x.typeCode === ShippingOptionCode.selfPickUp)
        .find((x) => x.isActive)?.isActive
    }
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");

  const isPickUpActive = watch("isSelfPickUpActive");
  const isActiveStanratOrExpress = (typeCode: ShippingOptionCode, regionCode: ShippingRegionCode) => {
    const isActive = watch(`${formControlShippingOptionName(typeCode)}.${formControlRegionName(regionCode)}.isActive`);
    return isActive;
  };

  const minPerion = (typeCode: ShippingOptionCode, regionCode: ShippingRegionCode) => {
    const minPeriod = watch(
      `${formControlShippingOptionName(typeCode)}.${formControlRegionName(regionCode)}.fromBusinessDays`
    );
    return minPeriod;
  };

  const onSubmit: SubmitHandler<ChangeShippingOptions> = (data: ChangeShippingOptions) => {
    data.standartShippingOption = {
      country: {
        fromBusinessDays: Number(data.standartShippingOption?.country?.fromBusinessDays) || 0,
        toBusinessDays: Number(data.standartShippingOption?.country?.toBusinessDays) || 0,
        orderPrice: {
          id: CurrencyCode.eur,
          value: Number(data.standartShippingOption?.country?.orderPrice?.value || 0)
        },
        isActive: data.standartShippingOption?.country?.isActive || false
      },
      europeanUnion: {
        fromBusinessDays: Number(data.standartShippingOption?.europeanUnion?.fromBusinessDays) || 0,
        toBusinessDays: Number(data.standartShippingOption?.europeanUnion?.toBusinessDays) || 0,
        orderPrice: {
          id: CurrencyCode.eur,
          value: Number(data.standartShippingOption?.europeanUnion?.orderPrice?.value || 0)
        },
        isActive: data.standartShippingOption?.europeanUnion?.isActive || false
      },
      worldwide: {
        fromBusinessDays: Number(data.standartShippingOption?.worldwide?.fromBusinessDays) || 0,
        toBusinessDays: Number(data.standartShippingOption?.worldwide?.toBusinessDays) || 0,
        orderPrice: {
          id: CurrencyCode.eur,
          value: Number(data.standartShippingOption?.worldwide.orderPrice?.value || 0)
        },
        isActive: data.standartShippingOption?.worldwide?.isActive || false
      }
    };
    data.expressShippingOption = {
      country: {
        fromBusinessDays: Number(data.expressShippingOption?.country?.fromBusinessDays) || 0,
        toBusinessDays: Number(data.expressShippingOption?.country?.toBusinessDays) || 0,
        orderPrice: {
          id: CurrencyCode.eur,
          value: Number(data.expressShippingOption?.country?.orderPrice?.value || 0)
        },
        isActive: data.expressShippingOption?.country?.isActive || false
      },
      europeanUnion: {
        fromBusinessDays: Number(data.expressShippingOption?.europeanUnion?.fromBusinessDays) || 0,
        toBusinessDays: Number(data.expressShippingOption?.europeanUnion?.toBusinessDays) || 0,
        orderPrice: {
          id: CurrencyCode.eur,
          value: Number(data.expressShippingOption?.europeanUnion?.orderPrice?.value || 0)
        },
        isActive: data.expressShippingOption?.europeanUnion?.isActive || false
      },
      worldwide: {
        fromBusinessDays: Number(data.expressShippingOption?.worldwide?.fromBusinessDays) || 0,
        toBusinessDays: Number(data.expressShippingOption?.worldwide?.toBusinessDays) || 0,
        orderPrice: {
          id: CurrencyCode.eur,
          value: Number(data.expressShippingOption?.worldwide?.orderPrice?.value || 0)
        },
        isActive: data.expressShippingOption?.worldwide?.isActive || false
      }
    };
    data.isSelfPickUpActive = data.isSelfPickUpActive;
    handleSubmitShippingOptions(data);
  };

  const handleSubmitShippingOptions = async (model: ChangeShippingOptions) => {
    if (isValid) {
      onClose();
      await dispatch(changeShippingOptions(model));
      await dispatch(getShippingOptions());
    }
  };

  const goToCompanyInfo = () => {
    navigate("/settings");
  };

  const shippingTabs = {
    standart: "standart",
    express: "express",
    pickUp: "pickUp"
  };

  const shippingRegionCodes = [ShippingRegionCode.country, ShippingRegionCode.EU, ShippingRegionCode.worldwide];

  const [value, setValue] = useState(shippingTabs.standart);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 24px 20px",
              minHeight: "500px"
            },
            ".MuiDialogTitle-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            },

            ".MuiPaper-root": {
              width: "900px",
              maxWidth: "900px",
              display: "flex",
              justifyContent: "center"
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <p className="heading-2">{translate("editShippingOptions")}</p>
        </DialogTitle>

        <DialogContent>
          <form id="form" onSubmit={handleSubmit(onSubmit)}>
            <TabContext value={value}>
              <Box>
                <TabList onChange={handleChange}>
                  <Tab label={translate("standart")} value={shippingTabs.standart} />
                  <Tab label={tCommon("express")} value={shippingTabs.express} />
                  <Tab label={translate("pickUp")} value={shippingTabs.pickUp} />
                </TabList>
              </Box>
              <TabPanelCustom value={shippingTabs.standart}>
                {shippingRegionCodes.map((code) => (
                  <ShippingOptionStandartOrExpress
                    key={code}
                    countryId={countryId}
                    register={register}
                    clearErrors={clearErrors}
                    regionCode={code}
                    typeCode={ShippingOptionCode.standart}
                    minPeriod={minPerion(ShippingOptionCode.standart, code)}
                    isActive={isActiveStanratOrExpress(ShippingOptionCode.standart, code)}
                    errors={errors}
                  />
                ))}
              </TabPanelCustom>
              <TabPanelCustom value={shippingTabs.express}>
                {shippingRegionCodes.map((code) => (
                  <ShippingOptionStandartOrExpress
                    key={code}
                    countryId={countryId}
                    register={register}
                    clearErrors={clearErrors}
                    regionCode={code}
                    minPeriod={minPerion(ShippingOptionCode.express, code)}
                    typeCode={ShippingOptionCode.express}
                    isActive={isActiveStanratOrExpress(ShippingOptionCode.express, code)}
                    errors={errors}
                  />
                ))}
              </TabPanelCustom>
              <TabPanelCustom value={shippingTabs.pickUp}>
                <Box
                  className="flex items-start pt-2 pb-4 px-4"
                  sx={{
                    background: isPickUpActive && warehouse ? colors.secondary.gray96 : "",
                    borderRadius: "8px"
                  }}
                >
                  {warehouse ? (
                    <>
                      <Checkbox {...register(`isSelfPickUpActive`)} checked={!warehouse ? false : isPickUpActive} />

                      <Box className="flex w-full gap-4" sx={{ marginTop: "9px" }}>
                        <Box className="text-16-bold" color="#333333" sx={{ width: "180px" }}>
                          {translate("fromWarehouse")}
                        </Box>
                        <Box>
                          {warehouse && <Box className="text-14-bold mt-1 mb-2">{tCommon("forms.address")}</Box>}
                          <Box className="text-14-regular" color="#333333">
                            {warehouse.address}
                            <br />
                            {compressWeekDays(warehouse.daysOfWeek || [])},{"\u00A0"}
                            {moment(warehouse.opensAt, "HH:mm:ss").format("HH:mm")}-
                            {moment(warehouse.closesAt, "HH:mm:ss").format("HH:mm")}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <Box>
                      <Box className="text-14-bold" color="#333333">
                        {translate("noWarehouseForPickUp")}
                      </Box>
                      <Box className="text-14-regular" color="#333333">
                        {translate("editWarehouseHint").replace("Company Info", "")}
                        <span className="text-14-bold" style={{ color: "#333333" }}>
                          {translate("goToCompanyInfo")}
                        </span>
                        {translate("editWarehouseHint").replace("You can edit warehouse address in Company Info tab (Business details section)", "")}
                      </Box>
                      <Button variant="text" className="text-14-medium" sx={{ padding: 0 }} onClick={goToCompanyInfo}>
                        {translate("goToCompanyInfo")}
                      </Button>
                    </Box>
                  )}
                </Box>
              </TabPanelCustom>
            </TabContext>
          </form>
        </DialogContent>

        <DialogActions className="flex">
          <Button variant="text" onClick={onClose} sx={{ width: "104px" }}>
            {tCommon("buttons.cancel")}
          </Button>

          <Button form="form" type="submit" variant="contained" className="ml-6" sx={{ width: "240px" }}>
            {translate("saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
export default ShippingDialog;